import { Mail, MessageSquare, UserCheck, FileText } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import InquiryForm from "@/components/InquiryForm";
import useTitle from "@/lib/useTitle";
import brand from "@/config/brand";

const STEPS = [
  {
    icon: MessageSquare,
    title: "Tell us about your project",
    text: "The form takes about two minutes. Share what you need in plain language — no technical knowledge required.",
    id: "step-tell",
  },
  {
    icon: UserCheck,
    title: "We follow up personally",
    text: "Expect a friendly reply from a real person within one business day.",
    id: "step-followup",
  },
  {
    icon: FileText,
    title: "You get a personalized quote",
    text: "A clear proposal outlining the one-time project cost and monthly care plan — no obligation, no surprises.",
    id: "step-quote",
  },
];

const Contact = () => {
  useTitle(
    "Request a Quote",
    "Request a personalized website quote from The Web Whisperer. Tell us about your project — we reply within one business day."
  );

  return (
    <main data-testid="contact-page" className="bg-cream pt-[72px]">
      <section className="relative overflow-hidden py-16 md:py-24">
        <div className="noise-overlay" aria-hidden="true" />
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-champagne/30 blur-[140px] pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-3xl">
            <p className="text-xs font-sans uppercase tracking-[0.28em] font-semibold text-turquoise-deep mb-6 flex items-center gap-3">
              <span className="w-10 h-px bg-turquoise inline-block" />
              Request a Quote
            </p>
            <h1 className="font-serif font-medium tracking-tight text-charcoal text-4xl sm:text-5xl lg:text-6xl leading-[1.08]">
              Let's Talk About <span className="italic text-turquoise">Your Website.</span>
            </h1>
            <p className="mt-6 text-lg font-sans text-taupe leading-relaxed max-w-2xl">
              Tell us a little about your business and what you need. We'll follow up personally with
              a tailored quote — no account needed, no pressure.
            </p>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* Left: what happens next + direct contact */}
            <Reveal delay={0.1} className="lg:col-span-5 space-y-8">
              <div>
                <h2 className="font-serif font-medium text-2xl text-charcoal mb-6">What happens next</h2>
                <ol className="space-y-6">
                  {STEPS.map((s, i) => (
                    <li key={s.id} data-testid={`contact-${s.id}`} className="flex items-start gap-4">
                      <div className="w-11 h-11 rounded-full bg-turquoise/10 border border-turquoise/30 flex items-center justify-center shrink-0">
                        <s.icon size={18} className="text-turquoise-deep" strokeWidth={1.75} />
                      </div>
                      <div>
                        <p className="font-sans font-semibold text-sm text-charcoal">
                          <span className="text-turquoise-deep mr-1.5">{i + 1}.</span>
                          {s.title}
                        </p>
                        <p className="mt-1 font-sans text-sm text-taupe leading-relaxed">{s.text}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="bg-cream-dark border border-taupe/15 rounded-2xl p-7">
                <p className="text-xs font-sans uppercase tracking-[0.2em] font-semibold text-turquoise-deep mb-4">
                  Prefer to reach out directly?
                </p>
                <ul className="space-y-3 font-sans text-sm text-charcoal/80">
                  <li className="flex items-center gap-3">
                    <Mail size={16} className="text-turquoise-deep shrink-0" />
                    <a data-testid="contact-email-link" href={`mailto:${brand.contact.email}`} className="hover:text-turquoise-deep transition-colors">
                      {brand.contact.email}
                    </a>
                  </li>
                </ul>
              </div>
            </Reveal>

            {/* Right: the form */}
            <Reveal delay={0.18} className="lg:col-span-7">
              <div className="bg-cream-dark border border-taupe/15 rounded-2xl p-6 sm:p-10 shadow-[0_20px_50px_-20px_rgba(32,32,32,0.12)]">
                <InquiryForm testPrefix="contact" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Contact;
